import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";
import { PluginCard } from "../components/common/PluginCard";
import { type Plugin } from "../context/PluginContext";
import { PluginCardSkeleton } from "../components/common/PluginCardSkeleton";
import { CategoryFilter } from "../components/common/CategoryFilter";
import { CategoryFilterSkeleton } from "../components/common/CategoryFilterSkeleton";

interface MasterCategory {
    id: number;
    name: string;
    slug: string;
    icon: string;
}

const ALL_CATEGORIES_SLUG = 'todos';
const ALL_CATEGORIES_NAME = 'Todos';

interface PluginsApiResponse {
    plugins: Plugin[];
    total: number;
}

type SortOption = 'newest' | 'oldest' | 'price-high' | 'price-low' | 'name-asc' | 'name-desc';

const sortOptions = [
    { value: 'newest' as SortOption, label: 'Mais Recentes', icon: '🆕' },
    { value: 'oldest' as SortOption, label: 'Mais Antigos', icon: '📅' },
    { value: 'price-high' as SortOption, label: 'Maior Preço', icon: '💰' },
    { value: 'price-low' as SortOption, label: 'Menor Preço', icon: '💵' },
    { value: 'name-asc' as SortOption, label: 'Nome (A-Z)', icon: '🔤' },
    { value: 'name-desc' as SortOption, label: 'Nome (Z-A)', icon: '🔡' },
];

export default function PluginsPage() {
    const [categories, setCategories] = useState<MasterCategory[]>([]);
    const [selectedCategorySlug, setSelectedCategorySlug] = useState<string>(ALL_CATEGORIES_SLUG);
    const [loadingCategories, setLoadingCategories] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [pluginsPerPage] = useState(8);
    const [totalPlugins, setTotalPlugins] = useState(0);
    const [displayedPlugins, setDisplayedPlugins] = useState<Plugin[]>([]);
    const [loadingPlugins, setLoadingPlugins] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<SortOption>('newest');
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    const fetchPlugins = useCallback(async (page: number, slug: string, sort: SortOption) => {
        setLoadingPlugins(true);
        setError(null);
        try {
            const params: { page: number; limit: number; categorySlug?: string; sortBy?: string } = {
                page,
                limit: pluginsPerPage,
                sortBy: sort,
            };
            if (slug !== ALL_CATEGORIES_SLUG) {
                params.categorySlug = slug;
            }

            const response = await apiClient.get<PluginsApiResponse>('/storefront/plugins', { params });
            

            setDisplayedPlugins(response.data.plugins);
            setTotalPlugins(response.data.total);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Falha ao carregar os plugins.');
        } finally {
            setLoadingPlugins(false);
        }
    }, [pluginsPerPage]);

    useEffect(() => {
        fetchPlugins(currentPage, selectedCategorySlug, sortBy);
    }, [currentPage, selectedCategorySlug, sortBy, fetchPlugins]);

    useEffect(() => {
        async function fetchCategories() {
            try {
                const response = await apiClient.get<MasterCategory[]>('/storefront/categories');
                const allCategoriesOption: MasterCategory = { id: 0, name: ALL_CATEGORIES_NAME, slug: ALL_CATEGORIES_SLUG, icon: '' };
                setCategories([allCategoriesOption, ...response.data]);
            } catch (err) {
                console.error("Erro ao buscar categorias:", err);
            } finally {
                setLoadingCategories(false);
            }
        }
        fetchCategories();
    }, []);

    const totalPages = Math.ceil(totalPlugins / pluginsPerPage);

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleCategoryChange = (slug: string) => {
        setSelectedCategorySlug(slug);
        setCurrentPage(1);
    }

    const handleSortChange = (option: SortOption) => {
        setSortBy(option);
        setShowSortDropdown(false);
        setCurrentPage(1);
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 relative overflow-hidden">
                {/* Padrão de fundo */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-10 -right-10 w-48 h-48 bg-white rounded-full"></div>
                    <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white rounded-full"></div>
                </div>

                <div className="container mx-auto px-4 py-10 relative">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-4 mb-3">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg">
                                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-3xl font-extrabold text-white leading-tight">
                                    Todos os Plugins
                                </h1>
                                <p className="text-sm text-blue-100">
                                    {totalPlugins > 0 && (
                                        <span>{totalPlugins} {totalPlugins === 1 ? 'plugin disponível' : 'plugins disponíveis'}</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-10">
                {/* Barra de Filtros e Ordenação */}
                <div className="mb-8">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <h2 className="text-lg font-bold text-gray-900">Filtros</h2>
                        </div>

                        {/* Dropdown de Ordenação */}
                        <div className="relative">
                            <button
                                onClick={() => setShowSortDropdown(!showSortDropdown)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-300 hover:border-brand-blue text-gray-700 font-medium rounded-xl transition-all duration-200 shadow-sm hover:shadow-md w-full lg:w-auto justify-between lg:justify-start"
                            >
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                                    </svg>
                                    <span className="text-sm font-semibold">Ordenar:</span>
                                    <span className="text-sm">{sortOptions.find(opt => opt.value === sortBy)?.label}</span>
                                </div>
                                <svg className={`w-4 h-4 text-gray-500 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {showSortDropdown && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setShowSortDropdown(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-20 animate-fade-in">
                                        {sortOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => handleSortChange(option.value)}
                                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-blue-50 transition-colors ${
                                                    sortBy === option.value ? 'bg-blue-50 text-brand-blue font-semibold' : 'text-gray-700'
                                                }`}
                                            >
                                                <span className="text-xl">{option.icon}</span>
                                                <span className="text-sm">{option.label}</span>
                                                {sortBy === option.value && (
                                                    <svg className="w-5 h-5 ml-auto text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Filtros de Categoria */}
                    <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
                        {loadingCategories ? (
                            Array.from({ length: 5 }).map((_, index) => <CategoryFilterSkeleton key={index} />)
                        ) : (
                            categories.map((cat) => (
                                <CategoryFilter
                                    key={cat.id}
                                    label={cat.name}
                                    icon={cat.icon}
                                    isActive={cat.slug === selectedCategorySlug}
                                    onClick={() => handleCategoryChange(cat.slug)}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Contador de resultados */}
                {!loadingPlugins && !error && displayedPlugins.length > 0 && (
                    <div className="mb-6">
                        <p className="text-sm text-gray-600">
                            Exibindo <span className="font-semibold text-gray-900">{displayedPlugins.length}</span> de <span className="font-semibold text-gray-900">{totalPlugins}</span> plugins
                            {selectedCategorySlug !== ALL_CATEGORIES_SLUG && (
                                <span> em <span className="font-semibold text-brand-blue">{categories.find(c => c.slug === selectedCategorySlug)?.name}</span></span>
                            )}
                        </p>
                    </div>
                )}

                {/* Grid de Plugins */}
                {loadingPlugins ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                        {Array.from({ length: pluginsPerPage }).map((_, index) => <PluginCardSkeleton key={index} />)}
                    </div>
                ) : error ? (
                    <div className="text-center py-20">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
                            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Erro ao carregar plugins</h3>
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : displayedPlugins.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum plugin encontrado</h3>
                        <p className="text-gray-600 mb-6">Não há plugins nesta categoria no momento.</p>
                        {selectedCategorySlug !== ALL_CATEGORIES_SLUG && (
                            <button
                                onClick={() => handleCategoryChange(ALL_CATEGORIES_SLUG)}
                                className="px-6 py-3 bg-brand-blue text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Ver todos os plugins
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-10">
                            {displayedPlugins.map((plugin) => (
                                <PluginCard key={plugin.id} plugin={plugin} />
                            ))}
                        </div>

                        {/* Paginação melhorada */}
                        {totalPages > 1 && (
                            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 py-8">
                                <button
                                    onClick={goToPreviousPage}
                                    disabled={currentPage === 1}
                                    className="group flex items-center gap-2 px-6 py-3 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-300 hover:border-brand-blue hover:text-brand-blue disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-700 transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                    <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Anterior
                                </button>

                                <div className="flex items-center gap-2">
                                    <div className="px-5 py-3 bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 text-white font-bold rounded-xl shadow-lg">
                                        <span className="text-sm">Página</span>
                                        <span className="text-xl mx-2">{currentPage}</span>
                                        <span className="text-sm">de {totalPages}</span>
                                    </div>
                                </div>

                                <button
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                    className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-600 hover:via-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                                >
                                    Próxima
                                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}